// src/components/MobileNav.jsx
// Mobile navigation with hamburger menu and slide-out drawer

import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { logout as apiLogout } from "../auth/Auth";
import {
  Menu,
  X,
  LayoutDashboard,
  Users,
  ListChecks,
  Settings,
  CalendarDays,
  Shield,
  BarChart3,
  Calendar,
  ClipboardList,
  LogOut,
  ChevronRight,
  AlertTriangle,
  PhoneCall,
  Building2,
  UserCog,
  MessageSquare,
  TrendingUp,
  FileText,
  Zap,
  DollarSign,
  Sunrise,
} from "lucide-react";
import { PendingApprovalsBadge } from "./PendingApprovals";

const brand = {
  navy: "#172A3A",
  gold: "#FFB800",
  slate: "#20344A",
};

export default function MobileNav() {
  const { t } = useTranslation();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [userRole, setUserRole] = useState(null);

  // Close menu on route change
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  // Fetch user role
  useEffect(() => {
    async function fetchUserRole() {
      try {
        const BASE = process.env.REACT_APP_API_URL || "";
        const res = await fetch(`${BASE}/auth/me`, { credentials: "include" });
        const json = await res.json();
        if (res.ok && json.ok) {
          setUserRole(json.user?.role);
        }
      } catch (err) {
        console.error("[MobileNav] Failed to fetch user role:", err);
      }
    }
    fetchUserRole();
  }, []);

  // Days until AEP
  const daysUntilAEP = (() => {
    const now = new Date();
    const year =
      now.getMonth() > 9 || (now.getMonth() === 9 && now.getDate() > 15)
        ? now.getFullYear() + 1
        : now.getFullYear();
    const target = new Date(year, 9, 15);
    const diffMs = target.getTime() - now.getTime();
    return Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
  })();

  // OEP badge
  const oepBadge = new Date().getMonth() <= 2 ? "OEP" : null;

  // Check if user is an agency role (owner or admin)
  const isAgencyRole = userRole && ["agency", "agency_owner", "agency_admin"].includes(userRole);

  // Check if user is founder/fmo level (sees admin features)
  const isFounderLevel = userRole && ["fmo", "founder"].includes(userRole);

  const navLinks = [
    // Agency roles get agency-specific navigation
    ...(isAgencyRole
      ? [
          { to: "/agency/dashboard", label: "Agency Dashboard", icon: Building2 },
          { to: "/daily-brief", label: "Daily Brief", icon: Sunrise },
          { to: "/agency/risk", label: "Risk Center", icon: AlertTriangle },
          { to: "/agency/agents", label: "Agent Management", icon: UserCog },
          { to: "/agency/clients", label: "All Clients", icon: Users },
          { to: "/agency/enrollment-tracker", label: "Enrollment Tracker", icon: FileText },
          { to: "/service", label: "Service Center", icon: PhoneCall },
          { to: "/agency/retention", label: "Retention Center", icon: TrendingUp },
        ]
      : [
          { to: "/dashboard", label: t("dashboard"), icon: LayoutDashboard },
          { to: "/daily-brief", label: "Daily Brief", icon: Sunrise },
          { to: "/risk", label: "Risk Management", icon: AlertTriangle },
          { to: "/communications", label: "Communications", icon: MessageSquare },
          { to: "/clients", label: t("clients"), icon: Users },
          { to: "/oep", label: t("oepHub"), icon: Shield, badge: oepBadge },
          { to: "/tasks", label: t("tasks"), icon: ListChecks },
          { to: "/calendar", label: t("calendar"), icon: Calendar },
          { to: "/settings", label: t("settings"), icon: Settings },
          { to: "/retention", label: "Retention", icon: TrendingUp },
          { to: "/enrollment-tracking", label: "Enrollments", icon: FileText },
          { to: "/service", label: "Service Center", icon: PhoneCall },
          { to: "/automations", label: "Automations", icon: Zap, badgeComponent: <PendingApprovalsBadge /> },
          { to: "/commissions", label: "Commissions", icon: DollarSign },
        ]
    ),
    // Founder/FMO level features (NOT shown to agency roles)
    ...(isFounderLevel
      ? [
          { to: "/founder", label: t("founderMetrics") || "Founder Metrics", icon: BarChart3 },
          { to: "/audit-logs", label: t("auditLogs") || "Audit Logs", icon: ClipboardList },
          { to: "/admin/users", label: "User Management", icon: UserCog },
        ]
      : []),
  ];

  const handleLogout = async () => {
    await apiLogout();
    window.location.href = "/login";
  };

  return (
    <>
      {/* Mobile Header Bar */}
      <header
        className="lg:hidden fixed top-0 left-0 right-0 z-50 h-14 flex items-center justify-between px-4 shadow-md"
        style={{ background: brand.navy }}
      >
        {/* Logo */}
        <Link to={isAgencyRole ? "/agency/dashboard" : "/dashboard"} className="flex items-center gap-2">
          <img
            src="/logo.png"
            alt="Pro IRP"
            className="w-8 h-8 rounded-full bg-white"
          />
          <span className="font-bold text-white text-lg">
            Pro <span style={{ color: brand.gold }}>IRP</span>
          </span>
        </Link>

        {/* Hamburger Button */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="p-2 rounded-lg text-white hover:bg-white/10 transition"
          aria-label="Toggle menu"
        >
          {isOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </header>

      {/* Overlay */}
      {isOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Slide-out Drawer */}
      <nav
        className={`lg:hidden fixed top-0 left-0 bottom-0 w-72 z-50 transform transition-transform duration-300 ease-in-out ${
          isOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        style={{ background: brand.navy }}
      >
        {/* Drawer Header */}
        <div className="h-14 flex items-center justify-between px-4 border-b border-white/10">
          <Link to={isAgencyRole ? "/agency/dashboard" : "/dashboard"} className="flex items-center gap-2" onClick={() => setIsOpen(false)}>
            <img
              src="/logo.png"
              alt="Pro IRP"
              className="w-8 h-8 rounded-full bg-white"
            />
            <span className="font-bold text-white text-lg">
              Pro <span style={{ color: brand.gold }}>IRP</span>
            </span>
          </Link>
          <button
            onClick={() => setIsOpen(false)}
            className="p-2 rounded-lg text-white hover:bg-white/10 transition"
          >
            <X size={20} />
          </button>
        </div>

        {/* Nav Links */}
        <div className="flex-1 overflow-y-auto py-4">
          <ul className="space-y-1 px-3">
            {navLinks.map(({ to, label, icon: Icon, badge, badgeComponent }) => {
              const isActive = location.pathname === to || location.pathname.startsWith(`${to}/`);
              return (
                <li key={to}>
                  <Link
                    to={to}
                    onClick={() => setIsOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition ${
                      isActive
                        ? "bg-white/10 text-white"
                        : "text-white/70 hover:bg-white/5 hover:text-white"
                    }`}
                  >
                    <Icon size={20} style={{ color: isActive ? brand.gold : "currentColor" }} />
                    <span className="flex-1">{label}</span>
                    {badgeComponent && (
                      <span className="ml-auto">{badgeComponent}</span>
                    )}
                    {badge && (
                      <span
                        className="px-2 py-0.5 rounded-full text-xs font-bold"
                        style={{ background: brand.gold, color: brand.navy }}
                      >
                        {badge}
                      </span>
                    )}
                    <ChevronRight size={16} className="text-white/30" />
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Logout Button */}
        <div className="p-4 border-t border-white/10">
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-bold transition"
            style={{ background: brand.gold, color: brand.navy }}
          >
            <LogOut size={18} />
            {t("logOut")}
          </button>
        </div>
      </nav>

      {/* Spacer for fixed header */}
      <div className="lg:hidden h-14" />
    </>
  );
}
